import { supabase } from '../lib/supabase';

export const packingListService = {
  // ✅ OPTIMIZED: Generate packing list with batch operations
  async generatePackingList(tripId) {
    try {
      const startTime = performance.now();
      
      // Call the optimized server-side function
      const { data, error } = await supabase
        ?.rpc('generate_packing_list_for_trip', {
          p_trip_id: tripId
        });

      if (error) throw error;

      const endTime = performance.now();
      const executionTime = Math.round(endTime - startTime);
      
      // Log performance for monitoring
      if (executionTime > 5000) {
        console.warn('⚠️ Slow packing list generation:', {
          tripId,
          executionTime: `${executionTime}ms`
        });
      } else {
        console.log('✅ Packing list generated in:', `${executionTime}ms`);
      }

      return data;
    } catch (error) {
      console.error('❌ Error generating packing list:', error);
      throw new Error(error?.message || 'Failed to generate packing list');
    }
  },

  // ✅ NEW: Batch insert packing items (uses new optimized function)
  async batchInsertPackingItems(tripId, userId, items) {
    try {
      const { data, error } = await supabase
        ?.rpc('batch_insert_packing_items', {
          p_trip_id: tripId,
          p_user_id: userId,
          p_items: items
        });

      if (error) throw error;

      console.log(`✅ Batch inserted ${data} packing items`);
      return data;
    } catch (error) {
      console.error('❌ Error batch inserting packing items:', error);
      throw error;
    }
  },

  // ✅ OPTIMIZED: Get packing list with performance monitoring
  async getPackingList(tripId) {
    try {
      const startTime = performance.now();
      
      const { data, error } = await supabase
        ?.from('trip_packing_lists')
        ?.select('*')
        ?.eq('trip_id', tripId)
        ?.order('item_category', { ascending: true })
        ?.order('item_priority', { ascending: false });

      if (error) throw error;

      const endTime = performance.now();
      const executionTime = Math.round(endTime - startTime);
      
      if (executionTime > 1000) {
        console.warn('⚠️ Slow packing list fetch:', {
          tripId,
          executionTime: `${executionTime}ms`,
          itemCount: data?.length
        });
      }

      return data?.map(item => ({
        id: item?.id,
        tripId: item?.trip_id,
        userId: item?.user_id,
        itemName: item?.item_name,
        itemCategory: item?.item_category,
        itemQuantity: item?.item_quantity,
        itemPriority: item?.item_priority,
        isChecked: item?.is_checked,
        autoGenerated: item?.auto_generated,
        createdAt: item?.created_at,
        updatedAt: item?.updated_at
      })) || [];
    } catch (error) {
      console.error('❌ Error fetching packing list:', error);
      throw error;
    }
  },

  /**
   * Toggle item checked status
   * @param {string} itemId - Packing list item UUID
   * @param {boolean} isChecked - New checked status
   * @returns {Promise<Object>} Updated item
   */
  async toggleItemChecked(itemId, isChecked) {
    if (!itemId) {
      throw new Error('Item ID is required');
    }

    const { data, error } = await supabase?.from('trip_packing_lists')?.update({ 
        is_checked: isChecked,
        updated_at: new Date()?.toISOString()
      })?.eq('id', itemId)?.select()?.single();

    if (error) {
      console.error('Error updating item:', error);
      throw new Error(`Failed to update item: ${error.message}`);
    }

    // Convert to camelCase
    return {
      id: data?.id,
      tripId: data?.trip_id,
      userId: data?.user_id,
      itemName: data?.item_name,
      itemCategory: data?.item_category,
      isChecked: data?.is_checked,
      itemQuantity: data?.item_quantity,
      itemPriority: data?.item_priority,
      autoGenerated: data?.auto_generated,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Add custom item to packing list
   * @param {string} tripId - Trip UUID
   * @param {string} itemName - Item name
   * @param {string} itemCategory - Item category
   * @param {number} itemQuantity - Item quantity (default: 1)
   * @param {string} itemPriority - Item priority (default: 'normal')
   * @returns {Promise<Object>} Created item
   */
  async addCustomItem(tripId, itemName, itemCategory, itemQuantity = 1, itemPriority = 'normal') {
    if (!tripId || !itemName || !itemCategory) {
      throw new Error('Trip ID, item name, and category are required');
    }

    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      throw new Error('Authentication required');
    }

    const { data, error } = await supabase?.from('trip_packing_lists')?.insert({
        trip_id: tripId,
        user_id: user?.id,
        item_name: itemName,
        item_category: itemCategory,
        item_quantity: itemQuantity,
        item_priority: itemPriority,
        auto_generated: false,
        is_checked: false
      })?.select()?.single();

    if (error) {
      console.error('Error adding item:', error);
      throw new Error(`Failed to add item: ${error.message}`);
    }

    // Convert to camelCase
    return {
      id: data?.id,
      tripId: data?.trip_id,
      userId: data?.user_id,
      itemName: data?.item_name,
      itemCategory: data?.item_category,
      isChecked: data?.is_checked,
      itemQuantity: data?.item_quantity,
      itemPriority: data?.item_priority,
      autoGenerated: data?.auto_generated,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Delete packing list item
   * @param {string} itemId - Item UUID
   * @returns {Promise<void>}
   */
  async deleteItem(itemId) {
    if (!itemId) {
      throw new Error('Item ID is required');
    }

    const { error } = await supabase?.from('trip_packing_lists')?.delete()?.eq('id', itemId);

    if (error) {
      console.error('Error deleting item:', error);
      throw new Error(`Failed to delete item: ${error.message}`);
    }
  },

  /**
   * Update item details
   * @param {string} itemId - Item UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated item
   */
  async updateItem(itemId, updates) {
    if (!itemId) {
      throw new Error('Item ID is required');
    }

    // Convert camelCase to snake_case for database
    const dbUpdates = {};
    if (updates?.itemName !== undefined) dbUpdates.item_name = updates?.itemName;
    if (updates?.itemCategory !== undefined) dbUpdates.item_category = updates?.itemCategory;
    if (updates?.itemQuantity !== undefined) dbUpdates.item_quantity = updates?.itemQuantity;
    if (updates?.itemPriority !== undefined) dbUpdates.item_priority = updates?.itemPriority;
    if (updates?.isChecked !== undefined) dbUpdates.is_checked = updates?.isChecked;

    dbUpdates.updated_at = new Date()?.toISOString();

    const { data, error } = await supabase?.from('trip_packing_lists')?.update(dbUpdates)?.eq('id', itemId)?.select()?.single();

    if (error) {
      console.error('Error updating item:', error);
      throw new Error(`Failed to update item: ${error.message}`);
    }

    // Convert to camelCase
    return {
      id: data?.id,
      tripId: data?.trip_id,
      userId: data?.user_id,
      itemName: data?.item_name,
      itemCategory: data?.item_category,
      isChecked: data?.is_checked,
      itemQuantity: data?.item_quantity,
      itemPriority: data?.item_priority,
      autoGenerated: data?.auto_generated,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  }
};